import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import useNotification from "../../hooks/useNotification";
import branch from "../../services/branch";
import queryDefault from "../../utils/queryDefault";

interface Branch {
  branch_id: number;
  branch_name: string;
  branch_group_name: string;
  place: string;
}

export default function OpnameStation() {
  const navigate = useNavigate();
  const notification = useNotification();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: branchesData, isLoading, error } = useQuery(
    "opname-stations",
    () => branch.getAllBranch({}),
    {
      ...queryDefault,
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to fetch stations",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const branches: Branch[] = useMemo(() => {
    const list = branchesData?.data || [];
    if (!searchTerm) return list;

    return list.filter(
      (b: Branch) =>
        b.branch_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.branch_group_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.place.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [branchesData, searchTerm]);

  const paginatedBranches = branches.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDetailClick = (branchId: number) => {
    navigate(`/app/stock-opname/station/${branchId}`);
  };

  if (isLoading) return <CircularProgress />;
  if (error) return null;

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          placeholder="Search by name, group, or place..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ flexGrow: 1 }}
        />
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 600 }}>Group</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Place</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedBranches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  No stations found
                </TableCell>
              </TableRow>
            ) : (
              paginatedBranches.map((branch) => (
                <TableRow key={branch.branch_id} hover>
                  <TableCell>{branch.branch_group_name}</TableCell>
                  <TableCell>{branch.branch_name}</TableCell>
                  <TableCell>{branch.place}</TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleDetailClick(branch.branch_id)}
                    >
                      Detail
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={branches.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
}
